import React, { useState } from 'react';
import { FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import './BuyBoxSidePanel.css';

interface BuyBoxSidePanelProps {
    tabColor?: string;
    tabName?: string;
    asinType?: string;
    asin?: string;
    validationInfo?: {
        timerEndTime: number | null;
        timerRemaining: number;
        notificationEnabled: boolean;
        onToggleNotification: () => void;
        onCopyUrl: () => void;
        urlCopied: boolean;
        validationUrl?: string;
    };
    onFullscreenChange?: (isFullscreen: boolean) => void;
}

const BuyBoxSidePanel: React.FC<BuyBoxSidePanelProps> = ({
    tabColor = '#CC0C39',
    tabName = 'Testing Instructions',
    asin,
    validationInfo,
    onFullscreenChange,
}) => {
    const [isMinimized, setIsMinimized] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [width, setWidth] = useState(38);
    const [isResizing, setIsResizing] = useState(false);
    const [showValidationInfo, setShowValidationInfo] = useState(true);
    const startXRef = React.useRef(0);
    const startWidthRef = React.useRef(38);

    const formatTimeRemaining = (ms: number): string => {
        const totalSeconds = Math.ceil(ms / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const toggleFullscreen = () => {
        const next = !isFullscreen;
        setIsFullscreen(next);
        onFullscreenChange?.(next);
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsResizing(true);
        startXRef.current = e.clientX;
        startWidthRef.current = width;
    };

    React.useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizing) return;
            e.preventDefault();
            const containerWidth =
                document.querySelector('.chat-with-browser-container')?.clientWidth || window.innerWidth;
            const deltaX = startXRef.current - e.clientX;
            const deltaPercent = (deltaX / containerWidth) * 100;
            const newWidth = startWidthRef.current + deltaPercent;
            if (newWidth >= 25 && newWidth <= 65) setWidth(newWidth);
        };
        const handleMouseUp = () => setIsResizing(false);

        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'ew-resize';
            document.body.style.userSelect = 'none';
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isResizing]);

    return (
        <>
            {isMinimized && (
                <div
                    className="buybox-panel-minimized"
                    onClick={() => setIsMinimized(false)}
                    style={{ borderColor: tabColor }}
                >
                    <div className="buybox-panel-minimized-icon" style={{ background: tabColor }}></div>
                    <div className="buybox-panel-minimized-info">
                        <span className="buybox-panel-minimized-title">{tabName}</span>
                        <span className="buybox-panel-minimized-url">Amazon Testing</span>
                    </div>
                </div>
            )}

            {!isMinimized && (
                <div
                    className={`browser-sidebar ${isClosing ? 'closing' : ''} ${isFullscreen ? 'fullscreen' : ''}`}
                    style={{ width: isFullscreen ? '100%' : `${width}%` }}
                >
                    {!isFullscreen && <div className="browser-resize-handle" onMouseDown={handleMouseDown} />}
                    {isResizing && <div className="browser-resize-overlay" />}

                    <div className="browser-sidebar-header" style={{ borderBottomColor: tabColor }}>
                        <div className="browser-sidebar-title-wrapper">
                            <div className="browser-sidebar-color-bar" style={{ backgroundColor: tabColor }} />
                            <span className="browser-sidebar-title">{tabName}</span>
                        </div>
                        <div className="browser-header-actions">
                            <button onClick={toggleFullscreen} className="browser-sidebar-fullscreen" title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
                                {isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                            </button>
                            {!isFullscreen && (
                                <button
                                    onClick={() => { setIsClosing(true); setTimeout(() => { setIsMinimized(true); setIsClosing(false); }, 400); }}
                                    className="browser-sidebar-close"
                                    title="Minimize"
                                >✕</button>
                            )}
                        </div>
                    </div>

                    {/* Validation info panel */}
                    {validationInfo && (
                        <div className={`browser-validation-panel ${showValidationInfo ? 'expanded' : 'collapsed'}`}>
                            <button className="browser-validation-toggle" onClick={() => setShowValidationInfo(!showValidationInfo)}>
                                <span className="validation-toggle-icon">{showValidationInfo ? '▼' : '▶'}</span>
                                <span className="validation-toggle-text">
                                    {validationInfo.timerEndTime && validationInfo.timerRemaining > 0
                                        ? `⏱️ ${formatTimeRemaining(validationInfo.timerRemaining)} remaining`
                                        : '✅ Ready to verify'}
                                </span>
                            </button>
                            {showValidationInfo && (
                                <div className="browser-validation-content">
                                    <div className="browser-validation-notes">
                                        {validationInfo.validationUrl && (
                                            <div className="validation-note-row">
                                                <div className="validation-note">
                                                    <strong>Validation URL</strong>
                                                    <a href={validationInfo.validationUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, wordBreak: 'break-all' }}>
                                                        {validationInfo.validationUrl}
                                                    </a>
                                                </div>
                                                <button className={`browser-copy-btn ${validationInfo.urlCopied ? 'copied' : ''}`} onClick={validationInfo.onCopyUrl}>
                                                    {validationInfo.urlCopied ? '✓ Copied' : '📋 Copy URL'}
                                                </button>
                                            </div>
                                        )}
                                        <div className="validation-note-row">
                                            <div className="validation-note">
                                                <strong>Propagation Delay</strong>
                                                G2S2 changes typically take ~15 minutes to propagate.
                                            </div>
                                            {validationInfo.timerEndTime && validationInfo.timerRemaining > 0 && (
                                                <button className={`browser-notify-btn ${validationInfo.notificationEnabled ? 'enabled' : ''}`} onClick={validationInfo.onToggleNotification}>
                                                    {validationInfo.notificationEnabled ? '🔔 Notify On' : '🔕 Notify Me'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Testing instructions */}
                    <div className="buybox-panel-content" style={{ padding: '20px', overflowY: 'auto' }}>
                        <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', fontSize: 13, lineHeight: 1.6, color: '#444' }}>
                            <div style={{ textAlign: 'center', marginBottom: 20 }}>
                                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#111' }}>How to Test on Amazon</h3>
                                <p style={{ margin: '4px 0 0', fontSize: 11, color: '#777' }}>Follow these steps to verify your G2S2 changes</p>
                            </div>

                            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 16 }}>
                                <div style={{ minWidth: 28, height: 28, borderRadius: '50%', background: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff', marginTop: 2 }}>1</div>
                                <div style={{ flex: 1 }}>
                                    <strong style={{ fontSize: 13, color: '#111' }}>Open the validation URL</strong>
                                    <p style={{ margin: '4px 0 0', fontSize: 12, color: '#555' }}>
                                        Click the URL above or copy it to your browser. The <code style={{ background: '#eef2f7', padding: '1px 5px', borderRadius: 3, fontSize: 11, color: '#2563eb' }}>g2s2StageVersionOverride</code> parameter loads your changes.
                                    </p>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 16 }}>
                                <div style={{ minWidth: 28, height: 28, borderRadius: '50%', background: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff', marginTop: 2 }}>2</div>
                                <div style={{ flex: 1 }}>
                                    <strong style={{ fontSize: 13, color: '#111' }}>Set your weblab in Neo</strong>
                                    <p style={{ margin: '4px 0 0', fontSize: 12, color: '#555' }}>
                                        Amazon DP doesn't support <code style={{ background: '#fef3e2', padding: '1px 5px', borderRadius: 3, fontSize: 11, color: '#92400e' }}>weblabOverride</code> in the URL. Set your treatment in the Neo weblab tool, then refresh.
                                    </p>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 20 }}>
                                <div style={{ minWidth: 28, height: 28, borderRadius: '50%', background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff', marginTop: 2 }}>3</div>
                                <div style={{ flex: 1 }}>
                                    <strong style={{ fontSize: 13, color: '#111' }}>Verify your changes</strong>
                                    <p style={{ margin: '4px 0 0', fontSize: 12, color: '#555' }}>
                                        With both the stage version override and Neo weblab active, your G2S2 changes should appear in the buy box.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default BuyBoxSidePanel;
